<template>
  <div class="chat">
    <div v-if="!conversation" class="chat__placeholder">
      <v-icon size="64" color="grey lighten-1">mdi-message-text-outline</v-icon>
      <p>{{ $t("chat.pick") }}</p>
    </div>

    <template v-else>
      <header class="chat__header">
        <v-btn
          v-if="showBack"
          icon
          class="mr-1"
          :aria-label="$t('chat.backToList')"
          @click="$emit('back')"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span
          class="chat__avatar"
          :class="`chat__avatar--${conversation.type}`"
        >
          <img v-if="conversation.avatar" :src="conversation.avatar" alt="" />
          <v-icon v-else-if="conversation.type === 'group'" color="white">
            mdi-account-group
          </v-icon>
          <v-icon v-else-if="conversation.type === 'support'" color="white">
            mdi-lifebuoy
          </v-icon>
          <span v-else>{{ conversation.initials }}</span>
        </span>
        <div class="chat__heading">
          <div class="chat__name">{{ conversation.title }}</div>
          <div class="chat__sub">{{ conversation.subtitle }}</div>
        </div>
        <v-btn
          v-if="
            conversation.type === 'support' &&
            conversation.key?.channel !== 'tutor' &&
            whatsappLink
          "
          :href="whatsappLink"
          target="_blank"
          rel="noopener noreferrer"
          small
          text
          color="#128C7E"
          class="chat__wpp"
        >
          <v-icon small left>mdi-whatsapp</v-icon>
          <span class="hidden-xs-only">WhatsApp</span>
        </v-btn>
      </header>

      <div ref="scroller" class="chat__messages" @scroll="onScroll">
        <div class="chat__more">
          <v-btn
            v-if="hasMore"
            small
            text
            :loading="loadingMore"
            @click="loadMore"
          >
            {{ $t("chat.loadOlder") }}
          </v-btn>
        </div>

        <page-skeleton v-if="loading" variant="chat" />

        <div v-else-if="!messages.length" class="chat__empty">
          {{
            conversation.type === "support"
              ? $t("chat.emptySupport")
              : $t("chat.empty")
          }}
        </div>

        <template v-for="(item, index) in decorated">
          <div
            v-if="item.showDay"
            :key="`day-${item.message.id}`"
            class="chat__day"
          >
            <span>{{ item.dayLabel }}</span>
          </div>
          <div
            :key="item.message.id || `tmp-${index}`"
            class="bubble-row"
            :class="{ 'bubble-row--mine': item.message.mine }"
          >
            <div
              class="bubble"
              :class="{
                'bubble--mine': item.message.mine,
                'bubble--support':
                  !item.message.mine && item.message.authorType === 'admin',
              }"
            >
              <div v-if="item.showAuthor" class="bubble__author">
                {{ item.message.authorName }}
              </div>
              <a
                v-if="item.message.file && item.message.file.url"
                href="#"
                class="bubble__image"
                @click.prevent="preview = item.message.file"
              >
                <img
                  :src="item.message.file.url"
                  :alt="item.message.file.name || $t('chat.sentImage')"
                  loading="lazy"
                  @error="$emit('image-error')"
                />
              </a>
              <div v-else-if="item.message.file" class="bubble__file-missing">
                <v-icon small>mdi-image-off-outline</v-icon>
                {{ $t("chat.imageUnavailable") }}
              </div>
              <div v-if="item.message.body" class="bubble__text">
                {{ item.message.body }}
              </div>
              <div class="bubble__time">
                {{ clock(item.message.createdAt) }}
              </div>
            </div>
          </div>
        </template>
      </div>

      <footer class="chat__composer">
        <div v-if="attachment" class="composer__attachment">
          <img :src="attachment.preview" :alt="$t('chat.attachmentPreview')" />
          <span class="composer__attachment-name">{{
            attachment.file.name
          }}</span>
          <v-btn
            icon
            small
            :aria-label="$t('chat.removeAttachment')"
            @click="clearAttachment"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="error" class="composer__error">{{ error }}</div>

        <div class="composer__row">
          <input
            ref="file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="d-none"
            @change="onFile"
          />
          <v-btn
            icon
            :disabled="sending"
            :aria-label="$t('chat.attachImage')"
            :title="$t('chat.attachHint')"
            @click="$refs.file.click()"
          >
            <v-icon>mdi-paperclip</v-icon>
          </v-btn>
          <textarea
            ref="input"
            v-model="draft"
            class="composer__input"
            rows="1"
            maxlength="4000"
            :placeholder="$t('chat.placeholder')"
            @keydown.enter.exact.prevent="submit"
            @input="autoGrow"
          />
          <v-btn
            fab
            small
            depressed
            dark
            :color="accent"
            :loading="sending"
            :disabled="!canSend"
            :aria-label="$t('chat.send')"
            @click="submit"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
        <div class="composer__hint">
          {{ $t("chat.safety") }}
          <span v-if="draft.length > 3500">{{ draft.length }}/4000</span>
        </div>
      </footer>
    </template>

    <v-dialog
      :value="!!preview"
      max-width="900"
      @input="(open) => !open && (preview = null)"
    >
      <v-card v-if="preview" class="pa-2 text-center">
        <img :src="preview.url" :alt="preview.name" class="chat__preview-img" />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :href="preview.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t("chat.openNewTab") }}
          </v-btn>
          <v-btn text @click="preview = null">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  formatDaySeparator,
  formatMessageClock,
  schoolDayKey,
} from "@/shared/messagesTime";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

export default {
  name: "ChatPanel",
  props: {
    conversation: { type: Object, default: null },
    messages: { type: Array, default: () => [] },
    hasMore: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    loadingMore: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    showBack: { type: Boolean, default: false },
    whatsappLink: { type: String, default: null },
    accent: { type: String, default: "#e8276f" },
  },
  data() {
    return {
      draft: "",
      attachment: null,
      error: "",
      preview: null,
      stickToBottom: true,
    };
  },
  computed: {
    canSend() {
      return (
        !this.sending && (this.draft.trim().length > 0 || !!this.attachment)
      );
    },
    // Separador de dia e nome do autor (so no chat da turma, e so quando muda
    // o autor), como no WhatsApp.
    decorated() {
      const isGroup = this.conversation?.type === "group";
      return this.messages.map((message, index) => {
        const previous = this.messages[index - 1];
        const day = schoolDayKey(message.createdAt);
        const showDay = !previous || schoolDayKey(previous.createdAt) !== day;
        const sameAuthor =
          previous &&
          !showDay &&
          previous.authorType === message.authorType &&
          String(previous.authorId) === String(message.authorId);
        return {
          message,
          showDay,
          dayLabel: showDay ? formatDaySeparator(message.createdAt) : "",
          showAuthor: isGroup && !message.mine && !sameAuthor,
        };
      });
    },
  },
  watch: {
    "conversation.id"() {
      this.reset();
    },
    messages(next, previous) {
      const prepended =
        previous?.length &&
        next.length > previous.length &&
        previous[0] &&
        next[0] &&
        String(next[0].id) !== String(previous[0].id) &&
        String(next[next.length - 1].id) ===
          String(previous[previous.length - 1].id);
      if (prepended) {
        this.keepPositionAfterPrepend();
      } else if (this.stickToBottom || !previous?.length) {
        this.scrollToBottom();
      }
    },
  },
  beforeDestroy() {
    this.clearAttachment();
  },
  methods: {
    clock: formatMessageClock,
    reset() {
      this.draft = "";
      this.error = "";
      this.clearAttachment();
      this.stickToBottom = true;
      this.scrollToBottom();
    },
    onScroll() {
      const el = this.$refs.scroller;
      if (!el) return;
      this.stickToBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scroller;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    loadMore() {
      const el = this.$refs.scroller;
      this.heightBeforePrepend = el ? el.scrollHeight : 0;
      this.$emit("load-more");
    },
    keepPositionAfterPrepend() {
      this.$nextTick(() => {
        const el = this.$refs.scroller;
        if (el && this.heightBeforePrepend) {
          el.scrollTop = el.scrollHeight - this.heightBeforePrepend;
        }
      });
    },
    autoGrow() {
      const el = this.$refs.input;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    },
    onFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      this.error = "";
      if (!file) return;
      if (!ACCEPTED.includes(file.type)) {
        this.error = this.$t("chat.badType");
        return;
      }
      if (file.size > MAX_BYTES) {
        this.error = this.$t("chat.tooBig");
        return;
      }
      this.clearAttachment();
      this.attachment = { file, preview: URL.createObjectURL(file) };
    },
    clearAttachment() {
      if (this.attachment?.preview)
        URL.revokeObjectURL(this.attachment.preview);
      this.attachment = null;
    },
    async submit() {
      if (!this.canSend) return;
      this.error = "";
      const payload = {
        body: this.draft.trim(),
        file: this.attachment?.file || null,
      };
      try {
        await new Promise((resolve, reject) =>
          this.$emit("send", payload, { resolve, reject })
        );
        this.draft = "";
        this.clearAttachment();
        this.stickToBottom = true;
        this.$nextTick(this.autoGrow);
      } catch (error) {
        this.error =
          error?.response?.data?.message || this.$t("chat.sendFailed");
      }
    },
  },
};
</script>

<style scoped>
.chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f7f3f8;
}
.chat__placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--dk-muted, #6b6472);
  gap: 8px;
}
.chat__header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: var(--dk-surface, #fff);
  border-bottom: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
}
.chat__avatar {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 10px;
  background: var(--msg-accent);
  color: #fff;
  font-weight: 800;
}
.chat__avatar--group {
  background: #5c6bc0;
}
.chat__avatar--support {
  background: #26a69a;
}
.chat__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.chat__heading {
  flex: 1 1 auto;
  min-width: 0;
}
.chat__name {
  font-weight: 800;
  color: var(--dk-ink, #231a2e);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__sub {
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6472);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 16px 16px;
}
.chat__more {
  text-align: center;
  min-height: 8px;
}
.chat__loading,
.chat__empty {
  text-align: center;
  padding: 32px 16px;
  color: var(--dk-muted, #6b6472);
}
.chat__day {
  text-align: center;
  margin: 14px 0 8px;
}
.chat__day span {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  color: var(--dk-muted, #6b6472);
  font-size: 0.72rem;
  font-weight: 700;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}
.bubble-row {
  display: flex;
  margin: 3px 0;
}
.bubble-row--mine {
  justify-content: flex-end;
}
.bubble {
  max-width: min(78%, 560px);
  padding: 7px 10px 4px;
  border-radius: 12px;
  border-top-left-radius: 4px;
  background: var(--dk-surface, #fff);
  color: var(--dk-ink, #231a2e);
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
}
.bubble--mine {
  background: var(--msg-bubble-mine);
  border-top-left-radius: 12px;
  border-top-right-radius: 4px;
}
.bubble--support {
  background: #e0f2f1;
}
.bubble__author {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--msg-accent);
  margin-bottom: 2px;
}
.bubble__text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.92rem;
}
.bubble__image img {
  display: block;
  max-width: 260px;
  max-height: 260px;
  width: 100%;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 4px;
}
.bubble__file-missing {
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
}
.bubble__time {
  text-align: right;
  font-size: 0.68rem;
  color: var(--dk-muted, #8a8490);
  margin-top: 2px;
}
.chat__composer {
  background: var(--dk-surface, #fff);
  border-top: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
  padding: 8px 12px 6px;
}
.composer__row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}
.composer__input {
  flex: 1 1 auto;
  resize: none;
  max-height: 140px;
  padding: 9px 12px;
  border-radius: 20px;
  background: var(--dk-paper, #f4f0f5);
  outline: none;
  font-size: 0.92rem;
  line-height: 1.35;
}
.composer__attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 8px;
}
.composer__attachment img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
}
.composer__attachment-name {
  flex: 1;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.composer__error {
  color: #c62828;
  font-size: 0.8rem;
  padding: 0 4px 6px;
}
.composer__hint {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: var(--dk-muted, #8a8490);
  padding: 4px 6px 0;
}
.chat__preview-img {
  max-width: 100%;
  max-height: 75vh;
}
</style>
